/**
 * @file index.jsx
 * @brief Admin-only page for managing platform-wide staff settings.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useUnifiedAuth as useAuth } from "../../hooks/useUnifiedAuth";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { createApiClient } from "../../api/axios";
import { useUserProfile } from "../../context/UserProfileContext";
import { useSiteSettings } from "../../context/SiteSettingsContext";
import styles from "./site-settings.module.css";

/**
 * Renders the staff settings form for admin users. Redirects non-admins away.
 * @returns {JSX.Element|null}
 */
export default function SiteSettings() {
    const { t } = useTranslation();
    const auth = useAuth();
    const navigate = useNavigate();
    const { profile } = useUserProfile();
    const accessToken = auth.user?.access_token;
    const api = useMemo(
        () => createApiClient({
            isAuthenticated: auth.isAuthenticated,
            user: accessToken ? { access_token: accessToken } : null,
        }),
        [accessToken, auth.isAuthenticated]
    );

    const { refreshSiteSettings } = useSiteSettings();
    const groupNames = profile?.groups || [];
    const isAdmin = groupNames.includes("admins");

    const [settings, setSettings] = useState(null);
    const [draft, setDraft] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        if (profile && !isAdmin) {
            navigate("/", { replace: true });
        }
    }, [isAdmin, navigate, profile]);

    const fetchSettings = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const { data } = await api.get("/settings/");
            setSettings(data);
            setDraft(data);
        } catch {
            setError(t("siteSettings.loadFailed"));
        } finally {
            setLoading(false);
        }
    }, [api, t]);

    useEffect(() => {
        if (isAdmin) fetchSettings();
    }, [isAdmin, fetchSettings]);

    const handleChange = (field, value) => {
        setDraft((prev) => ({ ...prev, [field]: value }));
        setSuccess(false);
    };

    const ssoChanged = settings && draft && (
        settings.oidc_authority !== draft.oidc_authority ||
        settings.oidc_client_id !== draft.oidc_client_id ||
        settings.oidc_algorithm !== draft.oidc_algorithm
    );

    const handleSave = async () => {
        try {
            setSaving(true);
            setError(null);
            setSuccess(false);
            const willReload = ssoChanged;
            const { data } = await api.put("/settings/", draft);
            setSettings(data);
            setDraft(data);
            setSuccess(true);
            refreshSiteSettings();
            if (willReload) {
                window.location.reload();
            }
        } catch {
            setError(t("siteSettings.saveFailed"));
        } finally {
            setSaving(false);
        }
    };

    const hasChanges = settings && draft && JSON.stringify(settings) !== JSON.stringify(draft);

    if (!isAdmin) return null;

    return (
        <div className={styles.container}>
            <div className={styles.hero}>
                <div className={styles.heroCopy}>
                    <p className={styles.eyebrow}>{t("siteSettings.eyebrow")}</p>
                    <h1 className={styles.pageTitle}>{t("siteSettings.title")}</h1>
                    <p className={styles.pageSubtitle}>{t("siteSettings.subtitle")}</p>
                </div>
            </div>

            {loading && (
                <div className={styles.panel}>
                    <div className={styles.skeletonStack}>
                        {[1, 2, 3, 4].map((i) => (
                            <div key={i} className={styles.skeletonRow} />
                        ))}
                    </div>
                </div>
            )}

            {error && <p className={styles.errorBanner}>{error}</p>}

            {!loading && draft && (
                <div className={styles.cardGrid}>
                    {/* ── General ── */}
                    <div className={styles.card}>
                        <h2 className={styles.cardTitle}>{t("siteSettings.generalSection")}</h2>
                        <p className={styles.cardHint}>{t("siteSettings.generalSectionHint")}</p>

                        <div className={styles.fieldGroup}>
                            <label className={styles.fieldLabel}>{t("siteSettings.siteName")}</label>
                            <input
                                className={styles.input}
                                type="text"
                                value={draft.site_name}
                                onChange={(e) => handleChange("site_name", e.target.value)}
                                maxLength={100}
                            />
                            <p className={styles.fieldHint}>{t("siteSettings.siteNameHint")}</p>
                        </div>

                        <div className={styles.fieldGroup}>
                            <label className={styles.fieldLabel}>{t("siteSettings.motd")}</label>
                            <textarea
                                className={styles.textarea}
                                value={draft.motd}
                                onChange={(e) => handleChange("motd", e.target.value)}
                                rows={3}
                            />
                            <p className={styles.fieldHint}>{t("siteSettings.motdHint")}</p>
                        </div>

                        <div className={styles.fieldGroup}>
                            <label className={styles.toggleLabel}>
                                <input
                                    type="checkbox"
                                    checked={draft.maintenance_mode}
                                    onChange={(e) => handleChange("maintenance_mode", e.target.checked)}
                                />
                                {t("siteSettings.maintenanceMode")}
                            </label>
                            <p className={styles.fieldHint}>{t("siteSettings.maintenanceModeHint")}</p>
                        </div>
                    </div>

                    {/* ── Listings ── */}
                    <div className={styles.card}>
                        <h2 className={styles.cardTitle}>{t("siteSettings.listingsSection")}</h2>
                        <p className={styles.cardHint}>{t("siteSettings.listingsSectionHint")}</p>

                        <div className={styles.fieldRow}>
                            <div className={styles.fieldGroup}>
                                <label className={styles.fieldLabel}>{t("siteSettings.maxListingImages")}</label>
                                <input
                                    className={styles.input}
                                    type="number"
                                    min={1}
                                    max={20}
                                    value={draft.max_listing_images}
                                    onChange={(e) => handleChange("max_listing_images", parseInt(e.target.value, 10) || 1)}
                                />
                            </div>

                            <div className={styles.fieldGroup}>
                                <label className={styles.fieldLabel}>{t("siteSettings.maxListingsPerUser")}</label>
                                <input
                                    className={styles.input}
                                    type="number"
                                    min={1}
                                    max={10000}
                                    value={draft.max_listings_per_user}
                                    onChange={(e) => handleChange("max_listings_per_user", parseInt(e.target.value, 10) || 1)}
                                />
                            </div>
                        </div>

                        <div className={styles.fieldGroup}>
                            <label className={styles.toggleLabel}>
                                <input
                                    type="checkbox"
                                    checked={draft.listings_public}
                                    onChange={(e) => handleChange("listings_public", e.target.checked)}
                                />
                                {t("siteSettings.listingsPublic")}
                            </label>
                            <p className={styles.fieldHint}>{t("siteSettings.listingsPublicHint")}</p>
                        </div>
                    </div>

                    {/* ── Authentication ── */}
                    <div className={styles.card}>
                        <h2 className={styles.cardTitle}>{t("siteSettings.authSection")}</h2>
                        <p className={styles.cardHint}>{t("siteSettings.authSectionHint")}</p>

                        <div className={styles.fieldGroup}>
                            <label className={styles.toggleLabel}>
                                <input
                                    type="checkbox"
                                    checked={draft.auth_sso_enabled}
                                    onChange={(e) => handleChange("auth_sso_enabled", e.target.checked)}
                                />
                                {t("siteSettings.authSsoEnabled")}
                            </label>
                            <p className={styles.fieldHint}>{t("siteSettings.authSsoEnabledHint")}</p>
                        </div>

                        <div className={styles.fieldGroup}>
                            <label className={styles.toggleLabel}>
                                <input
                                    type="checkbox"
                                    checked={draft.auth_local_enabled}
                                    onChange={(e) => handleChange("auth_local_enabled", e.target.checked)}
                                />
                                {t("siteSettings.authLocalEnabled")}
                            </label>
                            <p className={styles.fieldHint}>{t("siteSettings.authLocalEnabledHint")}</p>
                        </div>
                    </div>

                    {/* ── SSO ── */}
                    <div className={styles.card}>
                        <h2 className={styles.cardTitle}>{t("siteSettings.ssoSection")}</h2>
                        <p className={styles.cardHint}>{t("siteSettings.ssoSectionHint")}</p>

                        {ssoChanged && (
                            <p className={styles.warningBanner}>{t("siteSettings.ssoReloadWarning")}</p>
                        )}

                        <div className={styles.fieldGroup}>
                            <label className={styles.fieldLabel}>{t("siteSettings.oidcAuthority")}</label>
                            <input
                                className={styles.input}
                                type="url"
                                value={draft.oidc_authority}
                                onChange={(e) => handleChange("oidc_authority", e.target.value)}
                                placeholder={process.env.REACT_APP_OIDC_AUTHORITY}
                            />
                            <p className={styles.fieldHint}>{t("siteSettings.oidcAuthorityHint")}</p>
                        </div>

                        <div className={styles.fieldGroup}>
                            <label className={styles.fieldLabel}>{t("siteSettings.oidcClientId")}</label>
                            <input
                                className={styles.input}
                                type="text"
                                value={draft.oidc_client_id}
                                onChange={(e) => handleChange("oidc_client_id", e.target.value)}
                                placeholder={process.env.REACT_APP_OIDC_CLIENT_ID}
                            />
                            <p className={styles.fieldHint}>{t("siteSettings.oidcClientIdHint")}</p>
                        </div>

                        <div className={styles.fieldGroup}>
                            <label className={styles.fieldLabel}>{t("siteSettings.oidcAlgorithm")}</label>
                            <select
                                className={styles.input}
                                value={draft.oidc_algorithm}
                                onChange={(e) => handleChange("oidc_algorithm", e.target.value)}
                            >
                                {["RS256","RS384","RS512","ES256","ES384","ES512","PS256","PS384","PS512"].map((alg) => (
                                    <option key={alg} value={alg}>{alg}</option>
                                ))}
                            </select>
                            <p className={styles.fieldHint}>{t("siteSettings.oidcAlgorithmHint")}</p>
                        </div>
                    </div>

                    {/* ── Save bar ── */}
                    <div className={styles.actions}>
                        {success && <span className={styles.successMsg}>{t("siteSettings.saved")}</span>}
                        <button
                            className={styles.primaryButton}
                            onClick={handleSave}
                            disabled={saving || !hasChanges}
                        >
                            {saving ? t("common.saving") : t("common.save")}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
